/** "epicgames_S41_FNCSDivisionalCup_Division1_NAC" → "S41 FNCS Divisional Cup · Division1" */
export function prettyEventName(eventId: string): string {
  const stripped = eventId.replace(/^epicgames_/, "").replace(/_(NAC|NAW|EU|BR|ASIA|OCE|ME)$/i, "");
  return stripped
    .split("_")
    .map(part => part.replace(/([a-z])([A-Z])/g, "$1 $2"))
    .join(" · ");
}

/** "S41_FNCSDivisionalCup_Division1_Week1Day1_NAC" → "Week1Day1" (the part the event id doesn't carry) */
export function windowLabel(windowId: string, eventId: string): string {
  const eventParts = new Set(eventId.replace(/^epicgames_/, "").split("_"));
  const unique = windowId.split("_").filter(p => !eventParts.has(p));
  return unique.length > 0 ? unique.join(" ") : windowId;
}

export function relativeTime(iso: string): string {
  // ClickHouse returns "YYYY-MM-DD HH:MM:SS.mmm" (UTC, no zone marker)
  const normalized = iso.includes("T") ? iso : iso.replace(" ", "T") + "Z";
  const then = new Date(normalized).getTime();
  if (Number.isNaN(then)) return iso;
  const diff = Date.now() - then;
  const future = diff < 0;
  const abs = Math.abs(diff);
  const mins = Math.floor(abs / 60_000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const txt = days > 0 ? `${days}d` : hours > 0 ? `${hours}h` : mins > 0 ? `${mins}m` : "now";
  if (txt === "now") return "just now";
  return future ? `in ${txt}` : `${txt} ago`;
}

import type { MatchProgress } from "./api";

/** "chunk 12/41", "events 50/102", "waiting for manifest", … */
export function describeProgress(p: MatchProgress): string {
  switch (p.stage) {
    case "awaiting_manifest":
      return "waiting for manifest";
    case "parsing_data":
      return `chunk ${p.data_chunks}/${p.data_total || "?"}`;
    case "awaiting_chunks":
      return "live · waiting for chunks";
    case "parsing_events":
      return `events ${p.event_chunks}/${p.event_total || "?"}`;
  }
}

/** 0..1 across data chunks (the dominant phase), or null when unknowable. */
export function progressFraction(p: MatchProgress): number | null {
  if (p.stage === "awaiting_manifest") return null;
  if (p.stage === "parsing_events" && p.event_total > 0)
    return Math.min(p.event_chunks / p.event_total, 1);
  if (p.data_total > 0) return Math.min(p.data_chunks / p.data_total, 1);
  return null;
}

export function fmtDuration(seconds: number): string {
  if (seconds < 60) return `${Math.max(seconds, 0)}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** Match-clock ms → "12:34" (how deep into the game something happened). */
export function fmtGameClock(ms: number): string {
  const total = Math.max(Math.floor(ms / 1000), 0);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function fmtMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)}s`;
}

export function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "5:00 PM" split into the big clock and its small meridiem, for the schedule
 *  time column. In 24-hour locales the suffix is empty and `time` is "17:00". */
export function clockParts(iso: string | number): { time: string; suffix: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { time: "—", suffix: "" };
  const s = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const m = /^(.*?)[\s ]*([AP]\.?M\.?)$/i.exec(s);
  if (m?.[1] && m[2]) return { time: m[1], suffix: m[2].replace(/\./g, "").toUpperCase() };
  return { time: s, suffix: "" };
}

/** Coarse-to-fine countdown for the Verdict hero + next-up chip: days beyond a
 *  day, hours+minutes under a day, calm whole minutes under an hour, and the
 *  ticking m:ss only in the final five minutes — so the page's most dominant
 *  number stays calm at 40 minutes out and turns tense only when it truly
 *  matters. Clamps at 0; the parent re-derives windowState on the same tick, so
 *  the card flips to live rather than ever showing a frozen "0:00". */
export function countdown(deltaMs: number): string {
  const d = Math.max(0, deltaMs);
  if (d >= 86_400_000) {
    const days = Math.floor(d / 86_400_000);
    const hours = Math.floor((d % 86_400_000) / 3_600_000);
    return days >= 2 ? `${days}d` : `1d ${hours}h`;
  }
  if (d >= 3_600_000) {
    const h = Math.floor(d / 3_600_000);
    const m = Math.floor((d % 3_600_000) / 60_000);
    return `${h}h ${m}m`;
  }
  if (d >= 300_000) return `${Math.floor(d / 60_000)}m`;
  const m = Math.floor(d / 60_000);
  const s = Math.floor((d % 60_000) / 1000);
  return `${m}:${String(s).padStart(2, "0")}`;
}
